import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Card,
    CardContent,
    Typography,
    Chip,
    Avatar,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button
} from '@mui/material';
import { styled, useTheme, keyframes } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import GroupsIcon from '@mui/icons-material/Groups';

const gradient = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const BlogHeader = styled(Box)(({ theme }) => ({
    background: theme.palette.primary.main,
    backgroundSize: '400% 400%',
    animation: `${gradient} 15s ease infinite`,
    color: '#fff',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.05)',
        zIndex: 1
    }
}));

const BlogCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: 20,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    opacity: 0,
    transform: 'translateY(40px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    '&.animate-in': {
        opacity: 1,
        transform: 'translateY(0)'
    },
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
    }
}));

const FeaturedCard = styled(Card)(({ theme }) => ({
    borderRadius: 24,
    overflow: 'hidden',
    position: 'relative',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
    border: `2px solid ${theme.palette.primary.main}20`,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        border: `2px solid ${theme.palette.primary.main}40`
    }
}));

const CategoryTag = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.2s ease',
    '&.sustainability': {
        backgroundColor: theme.palette.primary.light,
        color: '#FFFFFF',
        border: 'none'
    },
    '&.community': {
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        color: '#1976D2',
        border: '1px solid rgba(25, 118, 210, 0.2)'
    },
    '&.tips': {
        backgroundColor: 'rgba(237, 108, 2, 0.1)',
        color: '#ED6C02',
        border: '1px solid rgba(237, 108, 2, 0.2)'
    }
}));

const StyledList = styled(List)(({ theme }) => ({
    '& .MuiListItem-root': {
        paddingLeft: 0,
        alignItems: 'flex-start',
        transition: 'all 0.2s ease',
        borderRadius: 8,
        '&:hover': {
            backgroundColor: 'rgba(76, 175, 80, 0.04)',
            transform: 'translateX(4px)'
        }
    },
    '& .MuiListItemIcon-root': {
        minWidth: 32,
        marginTop: 4
    }
}));

// Remove custom typography styling - use MUI defaults which inherit from theme

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 30,
    padding: theme.spacing(1.5, 4),
    fontWeight: 600,
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 0,
        height: 0,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        transform: 'translate(-50%, -50%)',
        transition: 'width 0.6s, height 0.6s'
    },
    '&:hover::before': { width: 300, height: 300 },
    '&.primary': {
        backgroundColor: '#34A853',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#2E7D32',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)'
        }
    },
    '&.outlined': {
        borderColor: '#34A853',
        color: '#34A853',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(52, 168, 83, 0.04)',
            borderColor: '#2E7D32',
            color: '#2E7D32',
            transform: 'translateY(-2px)'
        }
    }
}));

export default function Blog() {
    const theme = useTheme();
    const [animatedCards, setAnimatedCards] = useState(new Set());
    const cardRefs = useRef([]);
    const observerRef = useRef(null);

    const featuredPost = {
        title: "The Ultimate Guide to Sustainable Living: How Borrowed Helps You Save Money and the Environment",
        excerpt: "Sustainable living is more than a trend. Learn how borrowing keeps clutter down while cutting costs and emissions.",
        author: "Marcella Arthur",
        date: "July 2, 2025",
        readTime: "8 min read",
        category: "sustainability",
        image: "/images/featured-blog.jpg",
        slug: "sustainable-living-guide-f7a2"
    };

    /*
     * Previous demo posts kept for reference
     * {
     *   title: "10 Essential Items You Should Never Buy Again",
     *   excerpt: "From power tools to party supplies...",
     *   author: "Mike Rodriguez",
     *   date: "March 12, 2024",
     *   readTime: "5 min read",
     *   category: "tips",
     *   slug: "never-buy-again-items-b3f1"
     * },
     * { title: "Building Trust in Your Community: A Lender's Guide", ... },
     * { title: "The Environmental Impact of Sharing", ... },
     * { title: "Maximizing Your Earnings: Smart Lending Strategies", ... },
     * { title: "Community Success Stories: Real Impact, Real People", ... },
     * { title: "Seasonal Borrowing: What's Popular When", ... }
     */

    const blogPosts = [
        {
            title: 'Why Buy When You Can Borrow? Ten Everyday Items You Should Stop Buying',
            excerpt: 'A quick look at how borrowing common household goods saves money and space.',
            author: 'Borrowed Team',
            date: 'June 20, 2025',
            readTime: '6 min read',
            category: 'tips',
            slug: 'why-buy-when-you-can-borrow'
        },
        {
            title: 'Why Minimalism and Borrowing Go Hand in Hand',
            excerpt: 'Discover how a minimalist mindset pairs perfectly with the Borrowed community.',
            author: 'Borrowed Team',
            date: 'June 15, 2025',
            readTime: '5 min read',
            category: 'sustainability',
            slug: 'minimalism-and-borrowing'
        },
        {
            title: 'The Environmental Cost of Fast Fashion—and How Borrowing Can Help',
            excerpt: 'Fast fashion wastes resources. Borrowing outfits keeps style sustainable.',
            author: 'Borrowed Team',
            date: 'June 10, 2025',
            readTime: '7 min read',
            category: 'community',
            slug: 'fast-fashion-borrowing'
        },
        {
            title: 'Borrowing for Businesses: Competing with Amazon and Walmart by Offering Rentals in Your Store',
            excerpt: 'Small businesses can thrive by embracing rentals—here’s how Borrowed helps.',
            author: 'Borrowed Team',
            date: 'June 5, 2025',
            readTime: '8 min read',
            category: 'tips',
            slug: 'borrowing-for-businesses'
        }
    ];

    const categories = [
        { name: 'Sustainability', count: 8, icon: <PublicIcon />, color: 'success' },
        { name: 'Community', count: 12, icon: <GroupsIcon />, color: 'info' },
        { name: 'Tips & Guides', count: 15, icon: <TrendingUpIcon />, color: 'warning' }
    ];

    // Animation setup
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.dataset.index);
                        setTimeout(() => {
                            setAnimatedCards(prev => new Set([...prev, index]));
                        }, index * 150); // Staggered animation
                        observerRef.current.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '50px' }
        );

        // Observe all cards
        cardRefs.current.forEach((ref) => {
            if (ref) observerRef.current.observe(ref);
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    const handleCardClick = (slug) => {
        window.location.href = `/blog/${slug}`;
    };

    const renderCategoryTag = (category) => {
        const categoryLabels = {
            sustainability: 'Sustainability',
            community: 'Community',
            tips: 'Tips & Guides'
        };

        return (
            <CategoryTag className={category}>
                {categoryLabels[category] || category}
            </CategoryTag>
        );
    };

    return (
        <Box>
            {/* Blog Header */}
            <BlogHeader>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            fontWeight: 700
                        }}
                    >
                        Borrowed Blog
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{
                            opacity: 0.9,
                            maxWidth: 700,
                            mx: 'auto',
                            fontSize: { xs: '1.1rem', md: '1.3rem' }
                        }}
                    >
                        Stories, insights, and tips from the sharing economy community
                    </Typography>
                </Container>
            </BlogHeader>

            <Container maxWidth="lg" sx={{ py: 8 }}>
                {/* Featured Post */}
                <Box sx={{ mb: 8 }}>
                    <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
                        Featured Article
                    </Typography>
                    <FeaturedCard onClick={() => handleCardClick(featuredPost.slug)} sx={{ cursor: 'pointer' }}>
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                                {renderCategoryTag(featuredPost.category)}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                    <CalendarTodayIcon fontSize="small" />
                                    <Typography variant="body2">{featuredPost.date}</Typography>
                                    <AccessTimeIcon fontSize="small" sx={{ ml: 1 }} />
                                    <Typography variant="body2">{featuredPost.readTime}</Typography>
                                </Box>
                            </Box>

                            <Typography variant="h4" component="h3" gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 700, lineHeight: 1.3 }}>
                                {featuredPost.title}
                            </Typography>

                            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.7, color: 'text.secondary' }}>
                                {featuredPost.excerpt}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                                        <PersonIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {featuredPost.author}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Chief Business Officer
                                        </Typography>
                                    </Box>
                                </Box>
                                <StyledButton
                                    className="primary"
                                    size="large"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCardClick(featuredPost.slug);
                                    }}
                                >
                                    Read Full Article
                                </StyledButton>
                            </Box>
                        </CardContent>
                    </FeaturedCard>
                </Box>

                <Grid container spacing={4}>
                    {/* Main Content */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ mb: 6 }}>
                            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                                Coming Posts
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                                Stay updated with the latest insights from our community
                            </Typography>
                        </Box>

                        <Grid container spacing={4}>
                            {blogPosts.map((post, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <BlogCard
                                        ref={(el) => (cardRefs.current[index] = el)}
                                        data-index={index}
                                        className={animatedCards.has(index) ? 'animate-in' : ''}
                                        // onClick={() => handleCardClick(post.slug)}
                                        sx={{
                                            transitionDelay: `${index * 0.1}s`
                                        }}
                                    >
                                        <CardContent sx={{ p: 4 }}>
                                            <Box sx={{ mb: 3 }}>
                                                {renderCategoryTag(post.category)}
                                            </Box>

                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                                sx={{
                                                    mb: 2,
                                                    fontWeight: 600,
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {post.title}
                                            </Typography>

                                            <Typography variant="body2" paragraph color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                                                {post.excerpt}
                                            </Typography>

                                            <Divider sx={{ my: 3 }} />

                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: theme.palette.grey[400] }}>
                                                        <PersonIcon fontSize="small" />
                                                    </Avatar>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {post.author}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                                    <Typography variant="body2">{post.date}</Typography>
                                                    <Typography variant="body2">•</Typography>
                                                    <Typography variant="body2">{post.readTime}</Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </BlogCard>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Load More Button
                        <Box sx={{ textAlign: 'center', mt: 8 }}>
                            <StyledButton
                                className="outlined"
                                size="large"
                            >
                                Load More Articles
                            </StyledButton>
                        </Box> */}
                    </Grid>

                    {/* Sidebar */}
                    <Grid item xs={12} md={4}>
                        {/* Categories */}
                        <Paper sx={{ p: 4, borderRadius: 2, mb: 4, boxShadow: theme.shadows[3] }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Categories
                            </Typography>
                            <List>
                                {categories.map((category, index) => (
                                    <ListItem
                                        key={index}
                                        component={Button}
                                        sx={{
                                            borderRadius: 2,
                                            mb: 1,
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            color: 'text.primary'
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Box sx={{ color: `${category.color}.main` }}>
                                                {category.icon}
                                            </Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={category.name}
                                        // secondary={`${category.count} articles`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>

                        {/* Popular Topics */}
                        <Paper sx={{ p: 4, borderRadius: 2, mb: 4, boxShadow: theme.shadows[3] }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Popular Topics
                            </Typography>
                            <StyledList>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Smart borrowing strategies for beginners"
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Building trust in your neighborhood"
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Environmental benefits of sharing"
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Maximizing earnings from lending"
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Safety tips for borrowers and lenders"
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            </StyledList>
                        </Paper>

                        {/* Newsletter Signup
                        <Paper sx={{ p: 4, borderRadius: 2, bgcolor: '#34A853', color: 'white' }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Stay Updated
                            </Typography>
                            <Typography variant="body2" paragraph sx={{ opacity: 0.9 }}>
                                Get the latest articles and community insights delivered straight to your inbox.
                            </Typography>
                            <StyledButton
                                fullWidth
                                sx={{
                                    bgcolor: 'white',
                                    color: '#34A853',
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    '&:hover': {
                                        bgcolor: 'grey.100',
                                        transform: 'none'
                                    }
                                }}
                            >
                                Subscribe to Newsletter
                            </StyledButton>
                        </Paper> */}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
